import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import Hls from 'hls.js';

import { Globals } from '../../ressources/variables/globals';

/**
 * Service pour les opérations d'édition WinMedia
 * Note: L'authentification est gérée automatiquement via les cookies de session Laravel
 * grâce à l'intercepteur CredentialsInterceptor
 */
@Injectable()
export class EditService {

  serverEndpoint = Globals.serverEndpoint;

  audio;
  audioPlaying = '';

  audioHLS;
  audioHLSPlaying = '';
  hls: Hls | null = null;

  constructor(private http: HttpClient) {
    this.audio = new Audio();
  }

  updateProprietes(imedia: number, proprietes: string, mood3: string): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('imedia', imedia)
      .set('proprietes', proprietes)
      .set('mood3', mood3);
    return this.http.post(this.serverEndpoint + '/edit/updateProprietes', body.toString(), { headers }).toPromise();
  }

  getPreselectCategories(): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(this.serverEndpoint + '/api/getPreselectCategories', { headers }).toPromise();
  }

  getEditWinmediaCategories(): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(this.serverEndpoint + '/api/getEditWinmediaCategories', { headers }).toPromise();
  }

  searchWinmedia(term: string): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams().set('term', term);
    return this.http.post(this.serverEndpoint + '/edit/searchWinmedia', body.toString(), { headers }).toPromise();
  }

  /**
   * Récupère les sons d'une catégorie WinMedia pour le vote par catégorie.
   * @param category Valeur de la catégorie WinMedia (ex: 'Pop', 'Rock')
   */
  getWinmediaPreselectCategory(category: string): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = {
      category: category
    };
    return this.http.post(this.serverEndpoint + '/edit/getWinmediaPreselectCategory', body, { headers }).toPromise();
  }

  getWinmediaPreselectMonthlyPlaylist(stationsLabelIds: string[], year: number, month: number): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = {
      stationsLabelIds,  // Tableau de stations ['S01', 'S02', ...] ou ['W01'] ou []
      year: year.toString(),
      month: month.toString()
    };
    return this.http.post(this.serverEndpoint + '/edit/getWinmediaPreselectMonthlyPlaylist', body, { headers }).toPromise();
  }

  /**
   * Récupère les stations actives depuis radio_stations (Laravel)
   * Retourne: { winmedia_id, name }[]
   */
  getRadioStations(): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(this.serverEndpoint + '/edit/getRadioStations', {}, { headers }).toPromise();
  }

  // ============================================================================
  // Nouveau système de votes avec scores (1-10) - demi-étoiles
  // ============================================================================

  /**
   * Récupère tous les votes pour une playlist
   * @param idPlaylist ID de la playlist au format MM_YYYY
   */
  getPlaylistVotes(idPlaylist: string): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('idPlaylist', idPlaylist);
    return this.http.post(this.serverEndpoint + '/edit/getPlaylistVotes', body.toString(), { headers }).toPromise();
  }

  /**
   * Crée ou met à jour un vote
   * @param idPlaylist ID de la playlist au format MM_YYYY
   * @param imedia ID du média WinMedia
   * @param scoreVote Score de 1 à 10
   */
  upsertPlaylistVote(idPlaylist: string, imedia: number, scoreVote: number): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('idPlaylist', idPlaylist)
      .set('imedia', imedia.toString())
      .set('scoreVote', scoreVote.toString());
    return this.http.post(this.serverEndpoint + '/edit/upsertPlaylistVote', body.toString(), { headers }).toPromise();
  }

  /**
   * Supprime un vote
   * @param idPlaylist ID de la playlist au format MM_YYYY
   * @param imedia ID du média WinMedia
   */
  deletePlaylistVote(idPlaylist: string, imedia: number): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('idPlaylist', idPlaylist)
      .set('imedia', imedia.toString());
    return this.http.post(this.serverEndpoint + '/edit/deletePlaylistVote', body.toString(), { headers }).toPromise();
  }

  /**
   * Finalise une playlist (crée la ligne si elle n'existe pas)
   * Les positions ne sont pas stockées ici, mais lors du verrouillage
   * @param idPlaylist ID de la playlist au format MM_YYYY
   * @param idStation ID de la station
   */
  finalizePlaylist(idPlaylist: string, idStation: number): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('idPlaylist', idPlaylist)
      .set('idStation', idStation.toString());
    return this.http.post(this.serverEndpoint + '/edit/finalizePlaylist', body.toString(), { headers }).toPromise();
  }

  /**
   * Récupère une playlist finalisée
   * @param idPlaylist ID de la playlist au format MM_YYYY
   * @param idStation ID de la station
   */
  getFinalizedPlaylist(idPlaylist: string, idStation: number): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('idPlaylist', idPlaylist)
      .set('idStation', idStation.toString());
    return this.http.post(this.serverEndpoint + '/edit/getFinalizedPlaylist', body.toString(), { headers }).toPromise();
  }

  /**
   * Met à jour les positions d'une playlist finalisée
   * @param playlistId ID de la playlist finalisée (id dans du_playlists_finalized)
   * @param positions Liste des imedia séparés par virgule
   */
  updatePlaylistPositions(playlistId: number, positions: string): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams()
      .set('playlistId', playlistId.toString())
      .set('positions', positions);
    return this.http.post(this.serverEndpoint + '/edit/updatePlaylistPositions', body.toString(), { headers }).toPromise();
  }

  /**
   * Met à jour la limite de sélection d'une playlist finalisée
   * @param playlistId ID de la playlist finalisée
   * @param selectionLimit Nombre de titres sélectionnés (null pour supprimer la limite)
   */
  updateSelectionLimit(playlistId: number, selectionLimit: number | null): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    let body = new HttpParams()
      .set('playlistId', playlistId.toString());

    // Gérer le cas null explicitement
    if (selectionLimit === null) {
      body = body.set('selectionLimit', '');
    } else {
      body = body.set('selectionLimit', selectionLimit.toString());
    }

    return this.http.post(this.serverEndpoint + '/edit/updateSelectionLimit', body.toString(), { headers }).toPromise();
  }

  /**
   * Verrouille ou déverrouille les votes d'une playlist
   * @param playlistId ID de la playlist finalisée
   * @param isLocked true pour verrouiller, false pour déverrouiller
   * @param positions Positions optionnelles à sauvegarder lors du verrouillage
   */
  setPlaylistLock(playlistId: number, isLocked: boolean, positions: string | null): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    let body = new HttpParams()
      .set('playlistId', playlistId.toString())
      .set('isLocked', isLocked.toString());

    if (positions !== null) {
      body = body.set('positions', positions);
    }

    return this.http.post(this.serverEndpoint + '/edit/setPlaylistLock', body.toString(), { headers }).toPromise();
  }

  /**
   * Envoie la sélection finalisée : crée un brouillon sur Le Son Unique et met à jour
   * la position de diffusion dans Winmedia. Ne retente que les destinations pas encore réussies.
   */
  sendFinalizedPlaylist(playlistId: number): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const body = new HttpParams().set('playlistId', playlistId.toString());
    return this.http.post(this.serverEndpoint + '/edit/sendFinalizedPlaylist', body.toString(), { headers }).toPromise();
  }

  playextrait(src) {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.audio.src = src;
    this.audioPlaying = src;
    this.audio.load();
    this.audio.play();
    this.audio.onended = () => {
      this.audio.currentTime = 0;
      this.audioPlaying = '';
    };
  }

  stopextrait() {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.audioPlaying = '';
  }

  stopHLS() {
    // Arrêter la lecture audio
    if (this.audioHLS) {
      this.audioHLS.pause();
      this.audioHLS.currentTime = 0;
    }

    // Détruire l'instance HLS si elle existe
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }

    this.audioHLSPlaying = '';
  }

  /**
   * Lance la lecture HLS avec un élément audio HTML5 spécifique
   * @param src URL du flux HLS
   * @param audioElement Élément audio HTML5 du DOM
   */
  playHLSWithElement(src: string, audioElement: HTMLAudioElement) {
    // Arrêter toute lecture en cours
    this.stopHLS();

    // Stocker la référence à l'élément audio
    this.audioHLS = audioElement;
    this.audioHLSPlaying = src;

    if (Hls.isSupported()) {
      // Créer une nouvelle instance HLS
      this.hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });

      this.hls.loadSource(src);
      this.hls.attachMedia(audioElement);

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        audioElement.play().catch(err => {
          console.error('Erreur lors de la lecture:', err);
        });
      });

      this.hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('Erreur HLS:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Erreur réseau fatale, tentative de récupération...');
              this.hls?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Erreur média fatale, tentative de récupération...');
              this.hls?.recoverMediaError();
              break;
            default:
              console.error('Erreur fatale non récupérable');
              this.stopHLS();
              break;
          }
        }
      });
    } else if (audioElement.canPlayType('application/vnd.apple.mpegurl')) {
      // Support natif HLS (Safari)
      audioElement.src = src;
      audioElement.addEventListener('loadedmetadata', () => {
        audioElement.play().catch(err => {
          console.error('Erreur lors de la lecture:', err);
        });
      });
    } else {
      console.error('HLS n\'est pas supporté sur ce navigateur');
    }

    // Gérer la fin de lecture
    audioElement.onended = () => {
      this.audioHLSPlaying = '';
    };
  }
}
